import React, { useState, useEffect } from 'react';
import {
	CreditCard,
	RefreshCw,
	Calendar,
	AlertCircle,
	CheckCircle,
	Info,
	Settings,
	Edit3,
	Check,
	X,
} from 'lucide-react';
import Card from '../../../../components/ui/card';
import Button from '../../../../components/ui/button';
import Input from '../../../../components/ui/input';
import { useToast } from '../../../../components/ui/toast';
import { useSelector, useDispatch } from 'react-redux';
import Moment from 'react-moment';
import SublyApi from '../../../../helpers/Api.js';
import ManageSubscriptionsModal from '../../shared/ManageSubscriptionsModal.js';
import { getPrices } from '../../../../store/price.js';

const SubscriptionCard = ({ client, clientDataHook }) => {
	const dispatch = useDispatch();
	const { addToast } = useToast();
	const token = useSelector((st) => st.admin.token);
	const prices = useSelector((state) => state.price.allPrices);
	const [subscriptions, setSubscriptions] = useState([]);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [showManageModal, setShowManageModal] = useState(false);
	const [isEditingCustomerId, setIsEditingCustomerId] = useState(false);
	const [customerIdValue, setCustomerIdValue] = useState('');

	// Fetch subscriptions when client changes
	useEffect(() => {
		const fetchSubscriptions = async () => {
			if (client?.customerId) {
				try {
					const response = await SublyApi.getStripeSubscriptions(
						client.customerId,
						token
					);
					// Check if response has stripeSubs property or is the array directly
					const subscriptionsData = response.stripeSubs || response || [];
					setSubscriptions(subscriptionsData);
				} catch (error) {
					console.error('Error fetching subscriptions:', error);
					addToast({
						title: 'Error',
						description: 'Failed to load subscription information',
						type: 'error',
					});
				}
			}
		};

		fetchSubscriptions();
	}, [client, token, addToast]);

	// Get subscription status badge
	const getSubscriptionBadge = (subscription) => {
		// Check if canceled at period end but still active
		if (subscription.cancel_at_period_end) {
			return (
				<div className='inline-flex items-center justify-center text-orange-600 bg-orange-50 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap w-24'>
					<AlertCircle className='mr-1 h-3 w-3 flex-shrink-0' />
					<span>Canceling</span>
				</div>
			);
		}

		// Handle based on status
		switch (subscription.status) {
			case 'active':
				return (
					<div className='inline-flex items-center justify-center text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap w-24'>
						<CheckCircle className='mr-1 h-3 w-3 flex-shrink-0' />
						<span>Active</span>
					</div>
				);
			case 'canceled':
				return (
					<div className='inline-flex items-center justify-center text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap w-24'>
						<AlertCircle className='mr-1 h-3 w-3 flex-shrink-0' />
						<span>Canceled</span>
					</div>
				);
			default:
				return (
					<div className='inline-flex items-center justify-center text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap w-24'>
						<Info className='mr-1 h-3 w-3 flex-shrink-0' />
						<span>{subscription.status || 'Unknown'}</span>
					</div>
				);
		}
	};

	// Handle subscription refresh
	const handleRefreshSubscriptions = async () => {
		if (client?.customerId) {
			setIsRefreshing(true);
			try {
				const response = await SublyApi.getStripeSubscriptions(
					client.customerId,
					token
				);

				// Check if response has stripeSubs property or is the array directly
				const updatedSubscriptions = response.stripeSubs || response || [];
				setSubscriptions(updatedSubscriptions);

				addToast({
					title: 'Success',
					description: 'Subscription data refreshed successfully',
					type: 'success',
				});
			} catch (error) {
				console.error('Error refreshing subscriptions:', error);
				addToast({
					title: 'Error',
					description: 'Failed to refresh subscription data',
					type: 'error',
				});
			} finally {
				setIsRefreshing(false);
			}
		}
	};

	// Handle manage subscriptions modal
	const handleManageSubscriptions = () => {
		setShowManageModal(true);
	};

	// Fetch prices when modal opens
	useEffect(() => {
		if (showManageModal && client?.customerId) {
			dispatch(getPrices({ token }));
		}
	}, [showManageModal, dispatch, token, client]);

	// Customer ID inline edit handlers
	const handleEditCustomerId = () => {
		setCustomerIdValue(client?.customerId || '');
		setIsEditingCustomerId(true);
	};

	const handleSaveCustomerId = async () => {
		if (clientDataHook?.updateField) {
			const success = await clientDataHook.updateField(
				'customerId',
				customerIdValue.trim()
			);
			if (success) {
				setIsEditingCustomerId(false);
				setCustomerIdValue('');
			}
		}
	};

	const handleCancelCustomerId = () => {
		setIsEditingCustomerId(false);
		setCustomerIdValue('');
	};

	return (
		<>
			<Card className='border-l-2 border-l-primary/20'>
				<Card.Header className='border-b border-gray-100'>
					<div className='flex items-center justify-between'>
						<Card.Title className='flex items-center text-sm sm:text-base font-glacial uppercase tracking-wider text-gray-900'>
							<CreditCard className='w-4 h-4 mr-2 text-primary flex-shrink-0' />
							Subscriptions
						</Card.Title>
						<div className='flex space-x-1 flex-shrink-0'>
							<Button
								variant='ghost'
								size='sm'
								onClick={handleRefreshSubscriptions}
								disabled={isRefreshing}
								aria-label='Refresh subscriptions'
								className='text-gray-300 hover:text-primary px-2 py-1 text-xs transition-colors duration-200'>
								<RefreshCw
									className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
								/>
							</Button>
							<Button
								variant='ghost'
								size='sm'
								onClick={handleManageSubscriptions}
								aria-label='Manage subscriptions'
								className='text-gray-300 hover:text-primary px-2 py-1 text-xs transition-colors duration-200'>
								<Settings className='h-3.5 w-3.5' />
							</Button>
						</div>
					</div>
				</Card.Header>

				{/* Customer ID Field */}
				<div className='px-4 py-3 border-b border-gray-100'>
					<div className='flex items-start justify-between'>
						<div className='flex-1'>
							<label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
								Customer ID
							</label>
							{isEditingCustomerId ? (
								<div className='mt-1 space-y-2'>
									<Input
										value={customerIdValue}
										onChange={(e) => setCustomerIdValue(e.target.value)}
										placeholder='Enter Stripe customer ID (e.g., cus_...)'
										className='w-full text-sm'
									/>
									<div className='flex items-center space-x-2'>
										<Button
											size='sm'
											onClick={handleSaveCustomerId}>
											<Check className='w-3 h-3 mr-1' />
											Save
										</Button>
										<Button
											size='sm'
											variant='outline'
											onClick={handleCancelCustomerId}>
											<X className='w-3 h-3 mr-1' />
											Cancel
										</Button>
									</div>
								</div>
							) : (
								<div className='mt-1 text-sm text-gray-900'>
									{client?.customerId ? (
										<span className='font-mono'>{client.customerId}</span>
									) : (
										<span className='text-gray-400 italic'>Not set</span>
									)}
								</div>
							)}
						</div>
						{!isEditingCustomerId && (
							<Button
								size='sm'
								variant='ghost'
								onClick={handleEditCustomerId}
								className='ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0 mt-3'>
								<Edit3 className='w-3 h-3' />
							</Button>
						)}
					</div>
				</div>

				<Card.Content className='p-0'>
					{isRefreshing ? (
						<div className='p-6 text-center'>
							<div className='animate-spin h-8 w-8 mx-auto mb-2 border-4 border-primary border-t-transparent rounded-full'></div>
							<p className='text-gray-500'>Refreshing subscriptions...</p>
						</div>
					) : subscriptions && subscriptions.length > 0 ? (
						<div className='divide-y divide-gray-100'>
							{subscriptions.map((subscription, index) => {
								return (
									<div
										key={index}
										className='p-4 hover:bg-gray-50/50 transition-colors'>
										<div className='flex flex-col'>
											<div className='font-medium text-gray-900'>
												{subscription.productName || 'Unnamed Subscription'}
											</div>
											<div className='flex items-center justify-between mt-1'>
												<div className='text-sm text-gray-500 flex items-center'>
													<Calendar className='mr-1 h-3 w-3 flex-shrink-0' />
													<Moment format='LL'>
														{subscription.created * 1000}
													</Moment>
												</div>
												<div className='flex-shrink-0'>
													{getSubscriptionBadge(subscription)}
												</div>
											</div>
											{subscription.cancel_at_period_end && (
												<div className='text-xs text-orange-600 mt-1'>
													Will be canceled at the end of the billing period
												</div>
											)}
											{subscription.price && (
												<div className='mt-2 text-sm text-gray-700'>
													${(subscription.price / 100).toFixed(2)}/month
												</div>
											)}
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<div className='p-4 text-center text-gray-500'>
							<div className='py-6'>
								<CreditCard className='h-8 w-8 mx-auto mb-3 text-gray-300' />
								<div className='text-sm text-gray-500'>
									No active subscriptions
								</div>
								<div className='text-xs text-gray-400 mt-1'>
									This client does not have any active subscriptions
								</div>
							</div>
						</div>
					)}
				</Card.Content>
			</Card>

			{/* Manage Subscriptions Modal */}
			{showManageModal && client && (
				<ManageSubscriptionsModal
					user={client}
					subscriptions={subscriptions}
					availablePrices={prices || []}
					onClose={() => setShowManageModal(false)}
					onSubscriptionChange={handleRefreshSubscriptions}
					token={token}
				/>
			)}
		</>
	);
};

export default SubscriptionCard;
